import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../config/Api";
import { Review, CreateReviewRequest } from "../../types/reviewTypes";

interface ReviewState {
  reviews: Review[];
  loading: boolean;
  error: string | null;
  submitSuccess: boolean;
}

const initialState: ReviewState = {
  reviews: [],
  loading: false,
  error: null,
  submitSuccess: false,
};

// Fetch reviews for a specific product
export const fetchReviewsByProductId = createAsyncThunk<Review[], number>(
  "review/fetchReviewsByProductId",
  async (productId, { rejectWithValue }) => {
    try {
      const response = await api.get<Review[]>(`/api/reviews/product/${productId}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch reviews");
    }
  }
);

// Create a new product review
export const createProductReview = createAsyncThunk<Review, { request: CreateReviewRequest; jwt: string | null }>(
  "review/createProductReview",
  async ({ request, jwt }, { rejectWithValue }) => {
    try {
      const response = await api.post<Review>(`/api/reviews`, request, {
        headers: {
          Authorization: `Bearer ${jwt}`,
        },
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Failed to submit review");
    }
  }
);

// Delete a review
export const deleteProductReview = createAsyncThunk<number, { reviewId: number; jwt: string | null }>(
  "review/deleteProductReview",
  async ({ reviewId, jwt }, { rejectWithValue }) => {
    try {
      await api.delete(`/api/reviews/${reviewId}`, {
        headers: {
          Authorization: `Bearer ${jwt}`,
        },
      });
      return reviewId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Failed to delete review");
    }
  }
);

const reviewSlice = createSlice({
  name: "review",
  initialState,
  reducers: {
    resetReviewStatus: (state) => {
      state.submitSuccess = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Reviews
      .addCase(fetchReviewsByProductId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchReviewsByProductId.fulfilled, (state, action) => {
        state.loading = false;
        state.reviews = action.payload;
      })
      .addCase(fetchReviewsByProductId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Create Review
      .addCase(createProductReview.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.submitSuccess = false;
      })
      .addCase(createProductReview.fulfilled, (state, action) => {
        state.loading = false;
        state.reviews.unshift(action.payload);
        state.submitSuccess = true;
      })
      .addCase(createProductReview.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.submitSuccess = false;
      })
      // Delete Review
      .addCase(deleteProductReview.fulfilled, (state, action) => {
        state.reviews = state.reviews.filter((r) => r.id !== action.payload);
      });
  },
});

export const { resetReviewStatus } = reviewSlice.actions;
export default reviewSlice.reducer;